/* eslint-disable max-lines */
// internal deps
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { PaymentMethod, PaymentMethodLoadImpressionEventPayload, PaymentMethodMeta } from "@swiggy-private/payment-ui";

// helpers
import {
    isCardPaymentMethod,
    isNetBankingPaymentMethod,
    isNewCardPaymentMethod,
} from "@payment-client/helpers/PaymentHelper";
import { isWalletPaymentMethod } from "@payment-client/helpers/WalletHelper";
import { isSwiggyInstrumentPaymentMethod } from "@payment-client/helpers/SwiggyInstrumentsHelper";
import { isCODPaymentMethod } from "@payment-client/helpers/CodHelper";
import { isCredPaymentMethod } from "@payment-client/helpers/CredHelper";
import { isUpiIntentPaymentMethod } from "@payment-client/helpers/UpiHelper";
import { getResolvedPaymentsScreenName } from "@payment-client/analytics/helpers";
import { getBusinessLine, isBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";
import { isBillToCompanyPaymentMethod } from "@payment-client/helpers/BillToCompanyHelper";
import { isPODPaymentMethod } from "@payment-client/helpers/PayAfterDeliveryHelpers";

// analytics
import { onCredPayClickEvent, onCredPayButtonClickEvent } from "@payment-client/analytics/events/Cred";
import { onSavedCardPayButtonClickEvent } from "@payment-client/analytics/events/Card";
import { onAddNewCardPayClickEvent } from "@payment-client/analytics/events/Card";
import { onClickTurboPay } from "./TurboPayment";
import { clickPayAfterOrder } from "./PayOnDelivery";
import { onPODPayButtonClick, onPODPaymentClickEvent } from "./PayAfterDelivery";

// constants
import {
    CLICK_ACTIONS,
    SCREEN_NAME,
    IMPRESSION_ACTIONS,
    SCREEN_VIEW_ACTIONS,
} from "@payment-client/analytics/constants";
import { UPI_INTENT_METHOD_TYPE } from "@payment-client/analytics/constants/Payment";
import { PAYMENT_CODES } from "@swiggy-private/payment-types";

export const paymentScreenViewEvent = (screenViewEvent: AnalyticsContextValue["screenViewEvent"]): void => {
    screenViewEvent({
        category: SCREEN_NAME.PAYMENT,
    });
};

export const paymentConfirmScreenViewEvent = (screenViewEvent: AnalyticsContextValue["screenViewEvent"]): void => {
    screenViewEvent({
        category: SCREEN_NAME.PAYMENT_CONFIRMATION,
    });
};

export const paymentConfirmScreenV5ViewEvent = (impressionEvent: AnalyticsContextValue["impressionEvent"]): void => {
    impressionEvent({
        category: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_CONFIRMATION_V5,
    });
};

export const paymentConfirmScreenV5BlackOTPViewEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_CONFIRMATION,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_CONFIRMATION_V5_BLACK_OTP,
    });
};

export const onImpressionBoltPaymentConfirmation = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    context: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_CONFIRMATION,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_CONFIRMATION,
        context,
    });
};

export const onImpressionAffordableCartM2PaymentConfirmation = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    context: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_CONFIRMATION,
        // same event as bolt payment confirmation for 99 Store
        action: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_CONFIRMATION,
        context,
    });
};

export const onWalletPaymentClickEvent = (
    walletName: string,
    orderAmount: number,
    clickEvent: AnalyticsContextValue["clickEvent"],
    walletBalance?: string,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_WALLET,
        label: walletName,
        value: orderAmount,
        context: JSON.stringify({ orderAmount, walletBalance }),
    });
};

export interface onPaymentWalletLoadEventParams {
    walletName: string;
    orderAmount: number;
    impressionEvent: AnalyticsContextValue["impressionEvent"];
    walletBalance?: string;
}

export const onPaymentWalletLoadEvent = (params: onPaymentWalletLoadEventParams): void => {
    const { walletName, orderAmount, walletBalance, impressionEvent } = params;
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_WALLET,
        label: walletName,
        value: orderAmount,
        context: JSON.stringify({ orderAmount, walletBalance }),
    });
};

export const onPaymentMethodLoadEvent = (params: PaymentMethodLoadImpressionEventPayload): void => {
    const { methodName, orderAmount, methodSpecificData, impressionEvent } = params;
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_METHOD,
        label: methodName,
        value: orderAmount ?? "0",
        context: JSON.stringify(methodSpecificData),
    });
};

const onCODPaymentClickEvent = (orderAmount: number, clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_COD,
        value: orderAmount,
    });
};

const onCardPaymentClickEvent = (
    cardType: string,
    orderAmount: number,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    const category = getResolvedPaymentsScreenName();
    clickEvent({
        category,
        action: CLICK_ACTIONS.CLICK_CARD,
        label: cardType,
        value: orderAmount,
    });
};

const onNewCardPaymentClickEvent = (orderAmount: number, clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    const category = getResolvedPaymentsScreenName();
    clickEvent({
        category,
        action: CLICK_ACTIONS.CLICK_ADD_NEW_CARD,
        value: orderAmount,
        label: "add-card",
    });
};

const onNetbankingPaymentClickEvent = (
    bankCode: string,
    orderAmount: number,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    const category = getResolvedPaymentsScreenName();
    clickEvent({
        category,
        action: CLICK_ACTIONS.CLICK_NETBANKING,
        label: bankCode,
        value: orderAmount,
    });
};

const onUpiIntentPaymentClickEvent = (
    upiHandle: string,
    isPreferred: boolean,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    const category = getResolvedPaymentsScreenName();
    clickEvent({
        category,
        action: CLICK_ACTIONS.CLICK_UPI_INTENT_METHOD,
        label: upiHandle,
        context: isPreferred ? UPI_INTENT_METHOD_TYPE.PREFERRED : UPI_INTENT_METHOD_TYPE.NON_PREFERRED,
    });
};

export const onPayMethodClickEvent = (
    paymentMethodData: PaymentMethod<PaymentMethodMeta>,
    orderAmount: number,
    clickEvent: AnalyticsContextValue["clickEvent"],
    frictionLayoutId: string,
): void => {
    if (
        isWalletPaymentMethod(paymentMethodData) ||
        isSwiggyInstrumentPaymentMethod(paymentMethodData) ||
        isBillToCompanyPaymentMethod(paymentMethodData)
    ) {
        onWalletPaymentClickEvent(paymentMethodData.name, orderAmount, clickEvent, paymentMethodData?.balance);
    }
    if (isNetBankingPaymentMethod(paymentMethodData)) {
        onNetbankingPaymentClickEvent(paymentMethodData.nbCode, orderAmount, clickEvent);
    }
    if (isCODPaymentMethod(paymentMethodData)) {
        onCODPaymentClickEvent(orderAmount, clickEvent);
    }
    if (isPODPaymentMethod(paymentMethodData)) {
        onPODPaymentClickEvent(orderAmount, clickEvent);
    }
    if (isCardPaymentMethod(paymentMethodData)) {
        if (isNewCardPaymentMethod(paymentMethodData)) {
            onNewCardPaymentClickEvent(orderAmount, clickEvent);
        } else {
            onCardPaymentClickEvent(paymentMethodData.meta.cardBrand ?? "", orderAmount, clickEvent);
        }
    }
    if (isCredPaymentMethod(paymentMethodData)) {
        onCredPayClickEvent(paymentMethodData.groupName, frictionLayoutId, clickEvent);
    }
};

interface OnPayClickParams {
    paymentMethodData: PaymentMethod<PaymentMethodMeta>;
    frictionLayoutId: string;
    isTurboPaymentMethod?: boolean;
    slidePercent?: number;
    isPaymentMethodFacingDowntime?: boolean;
    groupCartId?: string;
    isTurboV2?: boolean;
    transactionAmount?: number;
}

export const onPayButtonClickEvent = (
    onPayClickParams: OnPayClickParams,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    const {
        frictionLayoutId,
        paymentMethodData,
        isTurboPaymentMethod,
        slidePercent = 0,
        isPaymentMethodFacingDowntime,
        groupCartId,
        isTurboV2,
        transactionAmount = 0,
    } = onPayClickParams;
    if (isTurboPaymentMethod) {
        if (isPaymentMethodFacingDowntime) {
            clickPayAfterOrder(clickEvent, true, slidePercent);
            return;
        }
        onClickTurboPay({
            paymentCode: paymentMethodData.paymentCode,
            clickEvent,
            slider: !isTurboV2,
            Width: slidePercent,
            groupCartId,
        });
    }

    if (isPaymentMethodFacingDowntime) {
        clickPayAfterOrder(clickEvent);
    }

    if (isCredPaymentMethod(paymentMethodData)) {
        onCredPayButtonClickEvent(paymentMethodData.groupName, frictionLayoutId, clickEvent);
    }
    if (isCardPaymentMethod(paymentMethodData)) {
        const category = getResolvedPaymentsScreenName();
        if (isNewCardPaymentMethod(paymentMethodData)) {
            onAddNewCardPayClickEvent(category, clickEvent);
        } else {
            onSavedCardPayButtonClickEvent(category, clickEvent);
        }
    }
    if (isUpiIntentPaymentMethod(paymentMethodData)) {
        onUpiIntentPaymentClickEvent(paymentMethodData.name, !!paymentMethodData.preferred, clickEvent);
    }

    if (isPODPaymentMethod(paymentMethodData)) {
        onPODPayButtonClick(transactionAmount, clickEvent);
    }
};
export const onMorePaymentOptionGroupImpressionEvent = (
    groupName: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        context: groupName,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_GROUP,
    });
};

export const onMorePaymentOptionGroupTooltipImpressionEvent = (
    groupName: string,
    text: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        context: groupName,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_GROUP_TOOLTIP,
        label: text,
    });
};

export const onViewMorePaymentsClickEvent = (
    frictionLayoutId: string,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_VIEW_MORE_PAYMENT_OPTIONS,
        context: frictionLayoutId,
    });
};

export const onViewMorePaymentGroupClickEvent = (
    groupName: string,
    frictionLayoutId: string,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    const category = getResolvedPaymentsScreenName();
    clickEvent({
        category,
        action: CLICK_ACTIONS.CLICK_PAYMENT_GROUP,
        context: `${groupName},${frictionLayoutId}`,
    });
};

export const morePaymentGroupsImpressionEvent = (
    frictionLayoutId: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_MORE_PAYMENT_GROUPS,
        context: frictionLayoutId,
    });
};

export const morePaymentMethodsImpressionEvent = (
    frictionLayoutId: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    const category = getResolvedPaymentsScreenName();
    impressionEvent({
        category,
        action: IMPRESSION_ACTIONS.IMPRESSION_MORE_PAYMENT_METHODS,
        context: frictionLayoutId,
    });
};

export const onPaymentListImpressionEvent = (
    cartId: string,
    planId: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    isIrctcCart = false,
): void => {
    const isSwiggyMoneyFlow = isBusinessLineSwiggyMoney();
    const category = getResolvedPaymentsScreenName();
    const action = isSwiggyMoneyFlow
        ? IMPRESSION_ACTIONS.IMPRESSION_SWIGGY_MONEY_PAYMENT_LIST
        : IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_LIST;
    impressionEvent({
        category,
        action,
        label: cartId,
        context: JSON.stringify({ planId }),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        extraParams: (isIrctcCart && JSON.stringify({ is_irctc_event: true })) || "",
    });
};

export const onMoreNetbankingClickEvent = (
    orderAmount: number,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    const category = getResolvedPaymentsScreenName();
    clickEvent({
        category,
        action: CLICK_ACTIONS.CLICK_MORE_BANKS,
        label: "more-banks",
        value: orderAmount,
    });
};

export const onSplitPayToggleEvent = ({
    selected,
    orderAmount,
    paymentCode,
    clickEvent,
}: {
    selected: boolean;
    orderAmount: number;
    paymentCode: string;
    clickEvent: AnalyticsContextValue["clickEvent"];
}): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action:
            paymentCode === PAYMENT_CODES.FLIPKART_SUPER_COIN
                ? CLICK_ACTIONS.CLICK_FLIPKART_COINS_OPTIN
                : CLICK_ACTIONS.CLICK_SWIGGY_PAY_OPTIN,
        label: selected ? 1 : 0,
        context: String(orderAmount),
    });
};

export const onLinkWalletClickEvent = (walletName: string, clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_LINK_WALLET,
        label: walletName,
    });
};

export const onUnableToFetchPaymentMethodEvent = (
    errorCode: number,
    errorMessage: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    const category = getResolvedPaymentsScreenName();
    impressionEvent({
        category,
        action: IMPRESSION_ACTIONS.IMPRESSION_UNABLE_TO_FETCH_METHODS,
        label: errorCode,
        context: errorMessage,
    });
};

export const onPlaceOrderErrorPopupEvent = (
    errorCode: number,
    errorMessage: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    const category = getResolvedPaymentsScreenName();
    impressionEvent({
        category,
        action: IMPRESSION_ACTIONS.IMPRESSION_PLACE_ORDER_ERROR_POPUP,
        label: errorCode,
        context: errorMessage,
    });
};

export const onAddMoneyClickEvent = (
    rechargeAmount: string,
    walletName: string,
    clickEvent: AnalyticsContextValue["clickEvent"],
    screenName: string = SCREEN_NAME.PAYMENT,
): void => {
    clickEvent({
        category: screenName,
        action: CLICK_ACTIONS.CLICK_ADD_MONEY,
        label: rechargeAmount,
        context: walletName,
    });
};

export const onAddMoneyImpressionEvent = (
    orderAmount: number,
    walletName: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    screenName: string = SCREEN_NAME.PAYMENT,
): void => {
    impressionEvent({
        category: screenName,
        action: IMPRESSION_ACTIONS.IMPRESSION_ADD_MONEY_WALLET,
        label: orderAmount,
        context: walletName,
    });
};

export const paymentBannerImpressionEvent = (
    bannerId: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESION_PAYMENT_BANNER,
        label: bannerId,
    });
};

export const paymentBannerClickEvent = (bannerId: string, clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_PAYMENT_BANNER,
        label: bannerId,
    });
};

export const onOrderPlacedEvent = (impressionEvent: AnalyticsContextValue["impressionEvent"]): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_ORDER_PLACED,
    });
};

export const onSwiggyMoneyRechargeSuccesful = (screenViewEvent: AnalyticsContextValue["screenViewEvent"]): void => {
    screenViewEvent({
        category: SCREEN_NAME.SWIGGY_MONEY_SUCCESS_SCREEN,
        action: SCREEN_VIEW_ACTIONS.VIEW_SWIGGY_MONEY_SUCCESS_SCREEN,
    });
};

export const onAddSwiggyMoneyClickEvent = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_SWIGGY_ADD_MONEY_WIDGET,
    });
};

export const onAddSwiggyMoneyImpressionEvent = (impressionEvent: AnalyticsContextValue["impressionEvent"]): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_ADD_SWIGGY_MONEY_WIDGET,
    });
};

export const onPayOnDeliveryPaymentDoneEvent = (screenViewEvent: AnalyticsContextValue["screenViewEvent"]): void => {
    screenViewEvent({
        category: SCREEN_NAME.PAY_ON_DELIVERY_PAYMENT_SUCCESS_SCREEN,
    });
};

export const unboxingBannerImpressionEvent = (
    value: string,
    context: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_MAW_BANNER,
        value,
        context,
    });
};

export const onPaymentPageImpression = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    context: {
        cartId: string;
        planId: string;
        couponCode?: string;
        couponApplied?: boolean;
        withSplitPay: boolean;
        billTotal: number;
    },
): void => {
    const category = getResolvedPaymentsScreenName();
    impressionEvent({
        category,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_PAGE,
        context: JSON.stringify({ ...context, businessLine: getBusinessLine(), category }),
    });
};

export const onUpiAppImpression = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    installedApps: string[],
    turboCheckoutWidget: boolean,
    isPaymentsPage = false,
): void => {
    impressionEvent({
        category: isPaymentsPage ? SCREEN_NAME.PAYMENT : SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_UPI_APP,
        label: JSON.stringify(installedApps),
        context: JSON.stringify({ turboCheckoutWidget: String(turboCheckoutWidget) }),
    });
};

export const impressionNudgePayFlipkartCoins = (
    isPaymentsPage: boolean,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: isPaymentsPage ? SCREEN_NAME.PAYMENT : SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_NUDGE_PAY_FLIPKART_COINS,
    });
};

export const impressionNudgeInvalidFlipkartCoins = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_NUDGE_INVALID_FLIPKART_COINS,
    });
};

export const payUsingOtherMethodsImpressionEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    couponCode?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAY_USING_OTHER_METHODS,
        context: JSON.stringify({ couponCode: couponCode }),
    });
};

export const payUsingOtherMethodsClickEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    couponCode?: string,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_PAY_USING_OTHER_METHODS,
        context: JSON.stringify({ couponCode: couponCode }),
    });
};
